/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useMediaQuery } from 'hooks/useMediaQuery';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CrmDataEnrichmentProblem.module.scss';

type Translate = (key: string) => string;

const getProblems = (t: Translate) => [
  {
    title: t('Enriched once, at import, then left alone'),
    body: t(
      'Registries publish changes every working day: names, addresses, directors, status, ownership. Your CRM holds the version it was handed on day one, and the gap widens every quarter nobody looks.',
    ),
    quote: t('A contract goes to an address the company left in March.'),
  },
  {
    title: t('The same customer, three times over'),
    body: t(
      'A web form, an imported list, a rep typing it in by hand. Three records, three spellings, no shared identifier between them. Reporting counts three accounts, and territory ownership becomes an argument.',
    ),
    quote: t('One customer, three owners, three forecasts.'),
  },
  {
    title: t('Company names are free text'),
    body: t(
      'ACME, Acme Ltd, ACME (UK) and Acme Limited are one legal entity. Without a registration number every match is a guess, and routing, scoring and dedupe all inherit that guess.',
    ),
    quote: t('Every downstream rule is built on the guess.'),
  },
  {
    title: t('Empty fields stall the workflow'),
    body: t(
      'Territory assignment reads country. Scoring reads turnover and headcount. Finance reads the legal name and the VAT number. When a third of records carry none of it, the rule silently skips them.',
    ),
    quote: t('A third of the database never enters the workflow.'),
  },
  {
    title: t('Enrichment nobody can trace'),
    body: t(
      'When compliance asks where a turnover figure came from, the name of a data vendor is not an answer. A field with no source behind it will not survive an audit, and it should not be steering a deal.',
    ),
    quote: t('The field does not survive the review.'),
  },
];

const MOBILE_QUERY_WIDTH = 1024;

export const CrmDataEnrichmentProblem = () => {
  const { t } = useInt();

  const isDesktop = useMediaQuery(MOBILE_QUERY_WIDTH);

  const PROBLEMS = getProblems(t);

  const [activeIndex, setActiveIndex] = useState(0);

  const tallRef = useRef<HTMLDivElement>(null);
  const curIndexRef = useRef(0);

  useEffect(() => {
    const tall = tallRef.current;

    if (!tall) {
      return undefined;
    }

    let ticking = false;

    const syncToScroll = () => {
      if (!window.matchMedia(`(min-width: ${MOBILE_QUERY_WIDTH}px)`).matches) {
        return;
      }

      const range = tall.offsetHeight - window.innerHeight;

      if (range <= 0) {
        return;
      }

      const y = -tall.getBoundingClientRect().top;
      const progress = Math.max(0, Math.min(1, y / range));
      const index = Math.min(PROBLEMS.length - 1, Math.floor(progress * PROBLEMS.length));

      if (index !== curIndexRef.current) {
        curIndexRef.current = index;
        setActiveIndex(index);
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          syncToScroll();
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const gotoIndex = (index: number) => {
    const tall = tallRef.current;

    if (tall && isDesktop) {
      const range = tall.offsetHeight - window.innerHeight;
      const top = tall.getBoundingClientRect().top + window.pageYOffset;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: top + range * ((index + 0.5) / PROBLEMS.length),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    curIndexRef.current = index;
    setActiveIndex(index);
  };

  const active = PROBLEMS[activeIndex];

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock align="left" title={t('A CRM decays quietly. You find out at the worst possible moment.')} />
      </div>

      {isDesktop === false ? (
        <div className={styles.stackList}>
          {PROBLEMS.map((problem, i) => (
            <div key={problem.title} className={styles.stackItem}>
              <span className={styles.railNum}>{`0${i + 1}`}</span>
              <h3 className={styles.stageTitle}>{problem.title}</h3>
              <p className={styles.stageBody}>{problem.body}</p>
              <div className={styles.stageQuote}>{problem.quote}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.tall} ref={tallRef}>
          <div className={styles.sticky}>
            <div className={styles.card}>
              <div className={styles.progress}>
                <span className={styles.progressCur}>{`0${activeIndex + 1}`}</span>
                <span className={styles.progressTrack}>
                  <span
                    className={styles.progressFill}
                    style={{ width: `${((activeIndex + 1) / PROBLEMS.length) * 100}%` }}
                  />
                </span>
                <span className={styles.progressTotal}>{`0${PROBLEMS.length}`}</span>
              </div>

              <div className={styles.grid}>
                <div className={styles.rail}>
                  {PROBLEMS.map((problem, i) => (
                    <button
                      key={problem.title}
                      type="button"
                      className={cn(styles.railItem, i === activeIndex && styles.railItemOn)}
                      onClick={() => gotoIndex(i)}
                    >
                      <span className={styles.railNum}>{`0${i + 1}`}</span>
                      <span className={styles.railTitle}>{problem.title}</span>
                    </button>
                  ))}
                </div>

                <div className={styles.stage}>
                  <h3 className={styles.stageTitle}>{active.title}</h3>
                  <p className={styles.stageBody}>{active.body}</p>
                  <div className={styles.stageQuote}>{active.quote}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};
